import { uid } from './bracket';
import { demo, demoProfielMetSpeler } from './demo';
import { client, supabaseIngesteld } from './supabase';
import { ligtVast } from './validate';
import type {
  LogRegel,
  Match,
  NewMatch,
  Permissie,
  Player,
  Poule,
  PouleLid,
  Profile,
  Rol,
  Tournament,
  Uitnodiging
} from './types';

/* ==========================================================================
   Alle toegang tot de gegevens loopt hierlangs.

   Twee bronnen achter dezelfde functies: Supabase als het project is
   ingesteld, anders de demomodus. De rest van de app merkt daar niets van;
   TanStack Query roept gewoon deze functies aan.

   Kolomnamen in de database staan in snake_case, de app werkt met camelCase.
   ========================================================================== */

export const demoModus = !supabaseIngesteld;

/* ---------- vertalen ---------- */

interface MatchRow {
  id: string;
  played_at: string;
  mode: Match['mode'];
  team_a: string[];
  team_b: string[];
  score_a: number;
  score_b: number;
  tournament_id: string | null;
  status?: Match['status'];
  ingevoerd_door?: string | null;
  telt_mee?: boolean;
  /** Meegelezen uit match_akkoord; supabase-js nest dat via de sleutelrelatie. */
  match_akkoord?: { profiel_id: string }[];
  created_at: string;
}

interface TournamentRow {
  id: string;
  poule_id: string | null;
  name: string;
  size: number;
  mode?: Tournament['mode'];
  teams?: string[][] | null;
  status: Tournament['status'];
  participants: string[];
  bracket: Tournament['bracket'];
  champion_id: string | null;
  created_at: string;
}

interface ProfileRow {
  bijnaam?: string | null;
  bijnaam_door?: string | null;
  id: string;
  name: string;
  username: string | null;
  role: string;
  status: Profile['status'];
  rating: number | string | null;
  photo_url: string | null;
  created_at: string;
  last_login_at: string | null;
  last_activity_at: string | null;
}

const naarMatch = (r: MatchRow): Match => ({
  id: r.id,
  playedAt: r.played_at,
  mode: r.mode,
  teamA: r.team_a ?? [],
  teamB: r.team_b ?? [],
  scoreA: r.score_a,
  scoreB: r.score_b,
  tournamentId: r.tournament_id,
  /* Potjes van vóór migratie 015 hebben geen status; die telden al mee. */
  status: r.status ?? 'bevestigd',
  akkoord: (r.match_akkoord ?? []).map((a) => a.profiel_id),
  /* Leeg bij potjes van voor migratie 018; die kan alleen een beheerder wissen. */
  ingevoerdDoor: r.ingevoerd_door ?? null,
  /* Potjes van voor migratie 027 telden allemaal mee. */
  teltMee: r.telt_mee ?? true,
  createdAt: r.created_at
});

const naarTournament = (r: TournamentRow): Tournament => ({
  id: r.id,
  pouleId: r.poule_id ?? null,
  name: r.name,
  size: r.size,
  /* Toernooien van voor migratie 024 kennen geen speelvorm; die waren 1v1. */
  mode: r.mode ?? '1v1',
  teams: r.teams ?? null,
  status: r.status,
  participants: r.participants ?? [],
  bracket: r.bracket ?? [],
  championId: r.champion_id,
  createdAt: r.created_at
});

const naarProfile = (r: ProfileRow): Profile => ({
  id: r.id,
  name: r.name,
  username: r.username,
  role: r.role,
  status: r.status,
  rating: Number(r.rating ?? 1200),
  photo: r.photo_url ?? '',
  createdAt: r.created_at,
  lastLoginAt: r.last_login_at,
  lastActivityAt: r.last_activity_at
});

/** Elk account is een speler; dit is dezelfde rij, anders bekeken. */
const profielAlsSpeler = (r: ProfileRow): Player => ({
  id: r.id,
  name: r.name,
  username: r.username,
  bijnaam: r.bijnaam ?? null,
  bijnaamDoor: r.bijnaam_door ?? null,
  photo: r.photo_url ?? '',
  isActive: r.status === 'actief',
  createdAt: r.created_at
});

/** Supabase-fouten zijn technisch; hier maken we er iets leesbaars van. */
function klaag(actie: string, fout: { message: string; code?: string } | null): never {
  const melding = fout?.message ?? 'onbekende fout';
  if (fout?.code === '42501') {
    throw new Error(`${actie}: geen rechten. Ben je ingelogd, en is migratie 006 gedraaid?`);
  }
  throw new Error(`${actie}: ${melding}`);
}

/* ---------- lezen ---------- */

export async function listPlayers(): Promise<Player[]> {
  if (demoModus) return demo.players();
  const { data, error } = await client().from('profiles').select('*').order('created_at');
  if (error) klaag('Spelers ophalen', error);
  return (data as ProfileRow[]).map(profielAlsSpeler);
}

/** De ratingstand binnen één poule, zoals de database hem heeft doorgerekend. */
export async function listPouleRatings(pouleId: string): Promise<{ profielId: string; rating: number; gespeeld: number; gewonnen: number }[]> {
  if (demoModus) return [];
  const { data, error } = await client().from('poule_ratings').select('*').eq('poule_id', pouleId);
  if (error) klaag('Stand ophalen', error);
  return (data ?? []).map((r: Rij) => ({
    profielId: r.profiel_id as string,
    rating: Number(r.rating ?? 1200),
    gespeeld: Number(r.gespeeld ?? 0),
    gewonnen: Number(r.gewonnen ?? 0)
  }));
}

/**
 * Een bijnaam geven of weghalen. Leeg betekent weghalen. De database bewaakt
 * de rest: niet bij jezelf, en alleen bij wie je in een poule tegenkomt.
 */
export async function zetBijnaam(spelerId: string, bijnaam: string): Promise<void> {
  if (demoModus) {
    demo.zetBijnaam(spelerId, bijnaam);
    return;
  }
  const { error } = await client().rpc('zet_bijnaam', { p_speler: spelerId, p_bijnaam: bijnaam });
  if (error) klaag('Bijnaam opslaan', error);
}

/** Favoriet is per lid: wat jij je hoofdcompetitie vindt. */
export async function zetFavoriet(pouleId: string, favoriet: boolean): Promise<void> {
  if (demoModus) {
    demo.zetFavoriet(pouleId, favoriet);
    return;
  }
  /* Via zet_favoriet() en niet rechtstreeks op poule_leden: daar mag alleen een
     beheerder schrijven, dus de update van een gewoon lid raakte nul rijen — en
     dat levert geen foutmelding op, waardoor de knop stil niets deed. */
  const { error } = await client().rpc('zet_favoriet', { p_poule: pouleId, p_favoriet: favoriet });
  if (error) klaag('Favoriet bijwerken', error);
}

export async function listMatches(pouleId?: string | null): Promise<Match[]> {
  if (demoModus) return demo.matches();
  let vraag = client().from('matches').select('*, match_akkoord(profiel_id)').order('played_at');
  if (pouleId) vraag = vraag.eq('poule_id', pouleId);
  const { data, error } = await vraag;
  if (error) klaag('Potjes ophalen', error);
  return (data as MatchRow[]).map(naarMatch);
}

export async function listTournaments(pouleId?: string | null): Promise<Tournament[]> {
  if (demoModus) return demo.tournaments();
  let vraag = client().from('tournaments').select('*').order('created_at');
  if (pouleId) vraag = vraag.eq('poule_id', pouleId);
  const { data, error } = await vraag;
  if (error) klaag('Toernooien ophalen', error);
  return (data as TournamentRow[]).map(naarTournament);
}

export async function listProfiles(): Promise<Profile[]> {
  if (demoModus) return [];
  const { data, error } = await client().from('profiles').select('*').order('created_at');
  if (error) klaag('Accounts ophalen', error);
  return (data as ProfileRow[]).map(naarProfile);
}

/* ---------- spelers ---------- */

/**
 * Losse spelers aanmaken kan niet meer: een speler is een account, en dat maakt
 * iemand zelf aan door zich aan te melden. Blijft staan voor de demomodus.
 */
export async function createPlayer(naam: string, foto?: File | null): Promise<Player> {
  if (!demoModus) {
    throw new Error('Een speler is een account: laat diegene zich aanmelden en keur het account goed.');
  }
  const speler: Player = {
    id: uid(),
    name: naam,
    username: null,
    bijnaam: null,
    bijnaamDoor: null,
    photo: foto ? await alsDataUrl(foto) : '',
    isActive: true,
    createdAt: new Date().toISOString()
  };
  return demo.addPlayer(speler);
}

export async function updatePlayer(speler: Player, foto?: File | null): Promise<Player> {
  const bijgewerkt = { ...speler };

  if (demoModus) {
    if (foto) bijgewerkt.photo = await alsDataUrl(foto);
    return demo.updatePlayer(bijgewerkt);
  }

  if (foto) bijgewerkt.photo = await uploadFoto(foto, speler.id);
  const { data, error } = await client()
    .from('profiles')
    .update({ name: bijgewerkt.name, photo_url: bijgewerkt.photo || null })
    .eq('id', speler.id)
    .select()
    .single();
  if (error) klaag('Speler bijwerken', error);
  return profielAlsSpeler(data as ProfileRow);
}

async function alsDataUrl(file: File): Promise<string> {
  return new Promise((klaar, mislukt) => {
    const lezer = new FileReader();
    lezer.onload = () => klaar(String(lezer.result));
    lezer.onerror = () => mislukt(new Error('Kon de foto niet lezen.'));
    lezer.readAsDataURL(file);
  });
}

async function uploadFoto(file: File, spelerId: string): Promise<string> {
  const ext = (file.name.split('.').pop() ?? 'jpg').toLowerCase();
  const pad = `${spelerId}.${ext}`;
  const { error } = await client().storage.from('fotos').upload(pad, file, { upsert: true });
  if (error) klaag('Foto uploaden', error);
  const { data } = client().storage.from('fotos').getPublicUrl(pad);
  return `${data.publicUrl}?v=${Date.now()}`;
}

/* ---------- potjes ---------- */

/**
 * Legt een potje vast. In Supabase gaat dat via record_match(), die het potje
 * invoegt en daarna alle ratings opnieuw doorrekent — nodig omdat uitslagen
 * met terugwerkende kracht mogen worden ingevoerd.
 */
export async function recordMatch(invoer: NewMatch): Promise<Match> {
  if (demoModus) {
    const ik = demoProfielMetSpeler().id;
    const speeltMee = [...invoer.teamA, ...invoer.teamB].includes(ik);
    return demo.addMatch({
      ...invoer,
      id: uid(),
      /* Net als in de database: een toernooipotje staat meteen vast, de rest
         wacht op de andere kant. */
      /* Wat niet meetelt hoeft ook niet bevestigd te worden. */
      status: invoer.tournamentId || invoer.teltMee === false ? 'bevestigd' : 'wacht',
      akkoord: !invoer.tournamentId && invoer.teltMee !== false && speeltMee ? [ik] : [],
      teltMee: invoer.teltMee !== false,
      /* In de demo voer jij het altijd zelf in, dus kun je het ook terugtrekken. */
      ingevoerdDoor: ik,
      createdAt: new Date().toISOString()
    });
  }

  if (!invoer.pouleId) throw new Error('Kies eerst een poule; een potje hoort ergens bij.');

  const { data, error } = await client().rpc('record_match', {
    p_id: uid(),
    p_poule_id: invoer.pouleId,
    p_played_at: invoer.playedAt,
    p_mode: invoer.mode,
    p_team_a: invoer.teamA,
    p_team_b: invoer.teamB,
    p_score_a: invoer.scoreA,
    p_score_b: invoer.scoreB,
    p_tournament_id: invoer.tournamentId,
    p_telt_mee: invoer.teltMee !== false
  });
  if (error) klaag('Uitslag vastleggen', error);
  return naarMatch(data as MatchRow);
}

/* ---------- akkoord op een uitslag ---------- */

/**
 * De stand erkennen. Zodra er van beide teams iemand getekend heeft, zet de
 * database het potje op bevestigd en rekent hij de ratings opnieuw door.
 */
export async function bevestigUitslag(id: string): Promise<{ id: string; bevestigd: boolean }> {
  if (demoModus) {
    demo.bevestig(id, demoProfielMetSpeler().id);
    return { id, bevestigd: true };
  }
  const { error } = await client().rpc('bevestig_uitslag', { p_match: id });
  if (error) klaag('Uitslag bevestigen', error);

  /* Of het potje nu écht rond is bepaalt de database: pas als van beide teams
     iemand tekende. Daarom achteraf navragen in plaats van aannemen. */
  const { data } = await client().from('matches').select('status').eq('id', id).single();
  return { id, bevestigd: data?.status === 'bevestigd' };
}

/**
 * De wachtrij laten rijpen: alles wat langer dan de bezwaartermijn (vier dagen)
 * staat te wachten telt vanaf nu mee. De database bewaakt de termijn; wij
 * porren hem alleen even wakker, want er draait geen cron.
 *
 * Geeft terug hoeveel uitslagen er zijn omgevallen — meestal nul.
 */
export async function vervalWachtendeUitslagen(): Promise<number> {
  if (demoModus) return demo.vervalWachtende();
  const { data, error } = await client().rpc('verval_wachtende_uitslagen');
  if (error) klaag('Wachtende uitslagen bijwerken', error);
  return Number(data ?? 0);
}

/**
 * De stand rechtzetten. Dat wist alle akkoorden: wie hem al goedkeurde, keurde
 * iets anders goed. Wie zelf meespeelde tekent meteen voor zijn eigen correctie.
 */
export async function wijzigUitslag(velden: {
  id: string;
  scoreA: number;
  scoreB: number;
  playedAt?: string | null;
}): Promise<void> {
  if (demoModus) {
    demo.wijzigScore(velden, demoProfielMetSpeler().id);
    return;
  }
  const { error } = await client().rpc('wijzig_uitslag', {
    p_match: velden.id,
    p_score_a: velden.scoreA,
    p_score_b: velden.scoreB,
    p_played_at: velden.playedAt ?? null
  });
  if (error) klaag('Uitslag aanpassen', error);
}

export async function deleteMatch(id: string): Promise<void> {
  if (demoModus) {
    demo.deleteMatch(id);
    return;
  }
  /* verwijder_uitslag() bewaakt alles op één plek en rekent de ratings zelf
     opnieuw door: de vaststeltermijn, een beheerder die altijd mag, en anders
     je eigen potje zolang de tegenpartij nog niet akkoord is. Elke weigering
     komt als een nette melding terug — de omweg met `.select()` om een stille
     DELETE te betrappen is daarmee niet meer nodig. */
  const { error } = await client().rpc('verwijder_uitslag', { p_match: id });
  if (error) klaag('Wedstrijd verwijderen', error);
}

/* ---------- toernooien ---------- */

export async function saveTournament(t: Tournament): Promise<void> {
  if (demoModus) {
    demo.saveTournament(t);
    return;
  }
  const { error } = await client()
    .from('tournaments')
    .upsert({
      id: t.id,
      poule_id: t.pouleId,
      name: t.name,
      size: t.size,
      mode: t.mode,
      /* De regel in migratie 024 wil bij 1v1 uitdrukkelijk geen ploegenlijst. */
      teams: t.mode === '2v2' ? t.teams : null,
      status: t.status,
      participants: t.participants,
      bracket: t.bracket,
      champion_id: t.championId,
      created_at: t.createdAt
    });
  if (error) klaag('Toernooi opslaan', error);
}

export async function deleteTournament(t: Tournament, eigenPotjes: Match[]): Promise<void> {
  if (demoModus) {
    demo.deleteTournament(t.id);
    return;
  }
  /* Alles of niets: een half verwijderd toernooi laat losse potjes achter die
     ineens als gewone uitslag in de historie staan. */
  if (eigenPotjes.some(ligtVast)) {
    throw new Error('Dit toernooi bevat potjes die vastliggen en kan niet meer verwijderd worden.');
  }
  for (const m of eigenPotjes) {
    const { error } = await client().from('matches').delete().eq('id', m.id);
    if (error) klaag('Toernooipotje verwijderen', error);
  }
  const { error } = await client().from('tournaments').delete().eq('id', t.id);
  if (error) klaag('Toernooi verwijderen', error);
  const { error: fout } = await client().rpc('recompute_ratings');
  if (fout) klaag('Ratings opnieuw doorrekenen', fout);
}

/* ---------- accounts ---------- */

export async function updateProfile(
  id: string,
  velden: {
    name?: string;
    username?: string | null;
    role?: string;
    status?: Profile['status'];
  }
): Promise<Profile> {
  const body: Partial<ProfileRow> = {};
  if (velden.name !== undefined) body.name = velden.name;
  if (velden.username !== undefined) body.username = velden.username;
  if (velden.role !== undefined) body.role = velden.role;
  if (velden.status !== undefined) body.status = velden.status;

  const { data, error } = await client().from('profiles').update(body).eq('id', id).select().single();
  if (error) klaag('Account bijwerken', error);
  return naarProfile(data as ProfileRow);
}

/* ---------- rollen en permissies ---------- */

export async function listRollen(): Promise<Rol[]> {
  if (demoModus) return [];
  const { data, error } = await client().from('rollen').select('*').order('ingebouwd', { ascending: false });
  if (error) klaag('Rollen ophalen', error);
  return (data ?? []) as Rol[];
}

export async function listPermissies(): Promise<Permissie[]> {
  if (demoModus) return [];
  const { data, error } = await client().from('permissies').select('*').order('soort').order('volgorde');
  if (error) klaag('Permissies ophalen', error);
  return (data ?? []) as Permissie[];
}

export async function listRolPermissies(): Promise<{ rolSleutel: string; permissieSleutel: string }[]> {
  if (demoModus) return [];
  const { data, error } = await client().from('rol_permissies').select('*');
  if (error) klaag('Permissies per rol ophalen', error);
  return (data ?? []).map((r: { rol_sleutel: string; permissie_sleutel: string }) => ({
    rolSleutel: r.rol_sleutel,
    permissieSleutel: r.permissie_sleutel
  }));
}

export async function bewaarRol(rol: Rol, nieuw: boolean): Promise<void> {
  const body = { sleutel: rol.sleutel, label: rol.label, omschrijving: rol.omschrijving };
  const { error } = nieuw
    ? await client().from('rollen').insert(body)
    : await client().from('rollen').update({ label: rol.label, omschrijving: rol.omschrijving }).eq('sleutel', rol.sleutel);
  if (error) klaag('Rol opslaan', error);
}

export async function verwijderRol(sleutel: string): Promise<void> {
  const { error } = await client().from('rollen').delete().eq('sleutel', sleutel);
  if (error) klaag('Rol verwijderen', error);
}

/** Zet de permissies van een rol in één keer goed: eerst leeg, dan opnieuw. */
export async function zetRolPermissies(rolSleutel: string, sleutels: string[]): Promise<void> {
  const weg = await client().from('rol_permissies').delete().eq('rol_sleutel', rolSleutel);
  if (weg.error) klaag('Permissies bijwerken', weg.error);
  if (!sleutels.length) return;
  const { error } = await client()
    .from('rol_permissies')
    .insert(sleutels.map((s) => ({ rol_sleutel: rolSleutel, permissie_sleutel: s })));
  if (error) klaag('Permissies bijwerken', error);
}

/* ---------- logboek ---------- */

export async function listLogboek(limiet = 200): Promise<LogRegel[]> {
  if (demoModus) return [];
  const { data, error } = await client()
    .from('logboek')
    .select('*')
    .order('moment', { ascending: false })
    .limit(limiet);
  if (error) klaag('Logboek ophalen', error);
  return (data ?? []).map((r: { id: number; moment: string; soort: LogRegel['soort']; profiel_id: string | null; omschrijving: string }) => ({
    id: r.id,
    moment: r.moment,
    soort: r.soort,
    profielId: r.profiel_id,
    omschrijving: r.omschrijving
  }));
}

/** Zuinig loggen: alleen inloggen, uitloggen en wijzigingen die ertoe doen. */
export async function log(soort: LogRegel['soort'], omschrijving: string): Promise<void> {
  if (demoModus) return;
  await client().rpc('log_gebeurtenis', { p_soort: soort, p_omschrijving: omschrijving });
}

export async function schoonLogboekOp(dagen: number): Promise<number> {
  const { data, error } = await client().rpc('schoon_logboek_op', { p_dagen: dagen });
  if (error) klaag('Logboek opschonen', error);
  return (data as number) ?? 0;
}

export async function markeerInlog(): Promise<void> {
  if (demoModus) return;
  await client().rpc('markeer_inlog');
}

/* ---------- gebruikscijfers ---------- */

export interface GebruikRij {
  dag: string;
  soort: 'mail_verstuurd' | 'mail_mislukt' | 'inlog' | 'bewerking';
  aantal: number;
}

export async function listGebruik(vanaf: string): Promise<GebruikRij[]> {
  if (demoModus) return [];
  const { data, error } = await client().from('gebruik_per_dag').select('*').gte('dag', vanaf).order('dag');
  if (error) klaag('Gebruikscijfers ophalen', error);
  return (data ?? []) as GebruikRij[];
}

/* ---------- verstuurde mail ---------- */

export interface MailRegel {
  id: number;
  moment: string;
  ontvanger: string;
  onderwerp: string;
  sjabloon: string;
  status: 'verstuurd' | 'mislukt';
  fout: string | null;
  externId: string | null;
}

/**
 * Hoeveel mail er vandaag al de deur uit is, en hoeveel er mag. Zie migratie
 * 021: boven de limiet weigert de Edge Function zelf, zodat de melding van ons
 * komt en niet van Resend.
 */
export async function mailTegoed(): Promise<{ vandaag: number; limiet: number }> {
  if (demoModus) return { vandaag: 0, limiet: 100 };
  const [vandaag, limiet] = await Promise.all([
    client().rpc('mail_vandaag'),
    client().rpc('mail_daglimiet')
  ]);
  if (vandaag.error) klaag('Maildagtelling ophalen', vandaag.error);
  if (limiet.error) klaag('Maildaglimiet ophalen', limiet.error);
  return { vandaag: Number(vandaag.data ?? 0), limiet: Number(limiet.data ?? 0) };
}

/** De laatste pogingen, geslaagd en mislukt. Schrijven doet alleen de functie. */
export async function listMailVerzending(limiet = 25): Promise<MailRegel[]> {
  if (demoModus) return [];
  const { data, error } = await client()
    .from('mail_verzending')
    .select('*')
    .order('moment', { ascending: false })
    .limit(limiet);
  if (error) klaag('Verstuurde mail ophalen', error);
  return (data ?? []).map((r: Rij) => ({
    id: Number(r.id),
    moment: r.moment as string,
    ontvanger: r.ontvanger as string,
    onderwerp: r.onderwerp as string,
    sjabloon: r.sjabloon as string,
    status: r.status as MailRegel['status'],
    fout: (r.fout as string) ?? null,
    externId: (r.extern_id as string) ?? null
  }));
}

/* ---------- poules ---------- */

type Rij = Record<string, unknown>;

const naarPoule = (r: Rij): Poule => ({
  id: r.id as string,
  naam: r.naam as string,
  omschrijving: (r.omschrijving as string) ?? null,
  prijs: (r.prijs as string) ?? null,
  aangemaaktDoor: (r.aangemaakt_door as string) ?? null,
  gearchiveerd: Boolean(r.gearchiveerd),
  createdAt: r.created_at as string
});

export async function listPoules(): Promise<Poule[]> {
  if (demoModus) return demo.poules();
  const { data, error } = await client().from('poules').select('*').order('created_at');
  if (error) klaag('Poules ophalen', error);
  return (data ?? []).map(naarPoule);
}

export async function maakPoule(velden: {
  naam: string;
  omschrijving: string | null;
  prijs: string | null;
  door: string;
}): Promise<Poule> {
  const { data, error } = await client()
    .from('poules')
    .insert({
      naam: velden.naam,
      omschrijving: velden.omschrijving,
      prijs: velden.prijs,
      aangemaakt_door: velden.door
    })
    .select()
    .single();
  if (error) klaag('Poule aanmaken', error);
  return naarPoule(data as Rij);
}

export async function wijzigPoule(
  id: string,
  velden: { naam?: string; omschrijving?: string | null; prijs?: string | null }
): Promise<void> {
  const { error } = await client().from('poules').update(velden).eq('id', id);
  if (error) klaag('Poule bijwerken', error);
}

export async function verwijderPoule(id: string): Promise<void> {
  const { error } = await client().from('poules').delete().eq('id', id);
  if (error) klaag('Poule verwijderen', error);
}

export async function listPouleLeden(): Promise<PouleLid[]> {
  if (demoModus) return demo.pouleLeden();
  const { data, error } = await client().from('poule_leden').select('*');
  if (error) klaag('Pouleleden ophalen', error);
  return (data ?? []).map((r: Rij) => ({
    pouleId: r.poule_id as string,
    profielId: r.profiel_id as string,
    rol: r.rol as PouleLid['rol'],
    favoriet: Boolean(r.favoriet),
    sinds: r.sinds as string
  }));
}

export async function listUitnodigingen(): Promise<Uitnodiging[]> {
  if (demoModus) return [];
  const { data, error } = await client().from('poule_uitnodigingen').select('*').order('created_at');
  if (error) klaag('Uitnodigingen ophalen', error);
  return (data ?? []).map((r: Rij) => ({
    id: r.id as string,
    pouleId: r.poule_id as string,
    profielId: r.profiel_id as string,
    door: (r.door as string) ?? null,
    status: r.status as Uitnodiging['status'],
    createdAt: r.created_at as string
  }));
}

/** Uitnodigen gaat op gebruikersnaam; e-mailadressen hoeven de browser niet in. */
export async function nodigUit(pouleId: string, gebruikersnaam: string): Promise<string> {
  const { data, error } = await client().rpc('nodig_uit', { p_poule: pouleId, p_username: gebruikersnaam });
  if (error) klaag('Uitnodigen', error);
  return data as string;
}

export async function beantwoordUitnodiging(id: string, akkoord: boolean): Promise<void> {
  const { error } = await client().rpc('beantwoord_uitnodiging', { p_uitnodiging: id, p_akkoord: akkoord });
  if (error) klaag('Uitnodiging beantwoorden', error);
}

export async function zetLidRol(pouleId: string, profielId: string, rol: PouleLid['rol']): Promise<void> {
  const { error } = await client()
    .from('poule_leden')
    .update({ rol })
    .eq('poule_id', pouleId)
    .eq('profiel_id', profielId);
  if (error) klaag('Rol in de poule aanpassen', error);
}

export async function verwijderLid(pouleId: string, profielId: string): Promise<void> {
  const { error } = await client()
    .from('poule_leden')
    .delete()
    .eq('poule_id', pouleId)
    .eq('profiel_id', profielId);
  if (error) klaag('Lid verwijderen', error);
}
